import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence
} from "firebase/auth";
import { auth } from "../firebase/firebase";

const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function SignInForm() {
  const navigate = useNavigate();

  const [email,      setEmail]      = useState(localStorage.getItem('rememberedEmail') || '');
  const [password,   setPassword]   = useState('');
  const [rememberMe, setRememberMe] = useState(!!localStorage.getItem('rememberedEmail'));
  const [showPw,     setShowPw]     = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [touched,    setTouched]    = useState({ email: false, password: false });

  const emailErr    = touched.email    && email    && !validateEmail(email)  ? 'Enter a valid email address' : '';
  const passwordErr = touched.password && password && password.length < 6    ? 'Password must be at least 6 characters' : '';
  const canSubmit   = validateEmail(email) && password.length >= 6 && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (!canSubmit) return;
    setError('');
    setLoading(true);
    try {
      await setPersistence(auth, browserSessionPersistence);
      rememberMe
        ? localStorage.setItem('rememberedEmail', email)
        : localStorage.removeItem('rememberedEmail');
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/users');
    } catch (err) {
      const map = {
        'auth/user-not-found':     'No account found with this email.',
        'auth/wrong-password':     'Incorrect password. Please try again.',
        'auth/too-many-requests':  'Too many attempts. Try again later.',
        'auth/invalid-credential': 'Invalid email or password.',
      };
      setError(map[err.code] || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="si-root">
      {/* ── left brand panel ── */}
      <aside className="si-panel" aria-hidden="true">
        <div className="si-panel__inner">
          <div className="si-logo">⬡ Look4Partner</div>
          <h1 className="si-panel__headline">Welcome<br />back.</h1>
          <p className="si-panel__sub">Sign in to pick up right where you left off.</p>
          <ul className="si-features">
            {['Secure & private', 'Real-time sync', 'Works everywhere'].map(f => (
              <li key={f}><span className="si-check">✓</span>{f}</li>
            ))}
          </ul>
        </div>
        <div className="si-panel__orb si-panel__orb--1" />
        <div className="si-panel__orb si-panel__orb--2" />
      </aside>

      {/* ── right form panel ── */}
      <main className="si-form-side">
        <button
          className="si-back"
          onClick={() => navigate(-1)}
          disabled={loading}
          aria-label="Go back"
        >
          ← Back
        </button>

        <div className="si-form-wrap">
          <div className="si-form-header">
            <h2 className="si-title">Sign in</h2>
            <p className="si-subtitle">
              New here?&nbsp;
              <button className="si-link" onClick={() => navigate('/signup')} disabled={loading}>
                Create an account
              </button>
            </p>
          </div>

          {error && (
            <div className="si-alert" role="alert">
              <span className="si-alert__icon">!</span>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* email */}
            <div className={`si-field ${touched.email && email ? (emailErr ? 'si-field--error' : 'si-field--ok') : ''}`}>
              <label className="si-label" htmlFor="si-email">Email address</label>
              <input
                id="si-email" className="si-input"
                type="email" autoComplete="email" autoFocus={!email}
                value={email}
                onChange={e => setEmail(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, email: true }))}
                disabled={loading}
                aria-describedby={emailErr ? 'si-email-err' : undefined}
              />
              {emailErr && <span id="si-email-err" className="si-field-msg">{emailErr}</span>}
            </div>

            {/* password */}
            <div className={`si-field ${touched.password && password ? (passwordErr ? 'si-field--error' : 'si-field--ok') : ''}`}>
              <div className="si-label-row">
                <label className="si-label" htmlFor="si-password">Password</label>
                <button type="button" className="si-link si-forgot" onClick={() => navigate('/forgot-password')} disabled={loading}>
                  Forgot password?
                </button>
              </div>
              <div className="si-input-wrap">
                <input
                  id="si-password" className="si-input"
                  type={showPw ? 'text' : 'password'} autoComplete="current-password" autoFocus={!!email}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, password: true }))}
                  disabled={loading}
                  aria-describedby={passwordErr ? 'si-pw-err' : undefined}
                />
                <button
                  type="button" className="si-pw-toggle"
                  onClick={() => setShowPw(s => !s)}
                  disabled={loading}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                >
                  {showPw ? '🙈' : '👁'}
                </button>
              </div>
              {passwordErr && <span id="si-pw-err" className="si-field-msg">{passwordErr}</span>}
            </div>

            {/* remember me */}
            <label className="si-remember">
              <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} disabled={loading} />
              <span className="si-remember__box" />
              <span className="si-remember__label">Remember my email</span>
            </label>

            {/* submit */}
            <button type="submit" className="si-btn-primary" disabled={!canSubmit} aria-busy={loading}>
              {loading ? <span className="si-spinner" aria-hidden="true" /> : 'Sign in →'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}